import { OAuth2 } from '@backstage/core-app-api';
import type {
  BackstageIdentityApi,
  OAuthApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  AppRootElementBlueprint,
  configApiRef,
  createApiRef,
  createFrontendModule,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  oauthRequestApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { signalApiRef } from '@backstage/plugin-signals-react';
import { useCallback, useEffect } from 'react';
import {
  createOidcSessionFetchApi,
  createOidcSessionLogoutSignalSubscription,
  getAppOidcSessionLogoutCoordinatorFromApis,
  getOidcSessionKeyFromToken,
  type OidcSessionLogoutSignalSubscriptionOptions,
} from './oidcSessionLogout';

/**
 * The OIDC auth API used by the ADP session logout feature.
 *
 * @public
 */
export const oidcAuthApiRef = createApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
>().with({
  id: 'oidc',
});

const oidcAuthApiExtension = ApiBlueprint.make({
  name: 'oidc-auth',
  params: defineParams =>
    defineParams({
      api: oidcAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          provider: {
            id: 'oidc',
            title: 'Dex',
            icon: () => null,
          },
          environment: configApi.getOptionalString('auth.environment'),
          defaultScopes: [
            'openid',
            'profile',
            'email',
            'offline_access',
            'audience:server:client_id:argocd',
          ],
          popupOptions: {
            size: {
              fullscreen: false,
            },
          },
        }),
    }),
});

const OidcSessionLogoutSubscription = (
  props: OidcSessionLogoutSignalSubscriptionOptions,
) => {
  const { signals, coordinator, getCurrentSessionKey } = props;

  useEffect(() => {
    const subscription = createOidcSessionLogoutSignalSubscription({
      signals,
      coordinator,
      getCurrentSessionKey,
    });

    return () => subscription.unsubscribe();
  }, [signals, coordinator, getCurrentSessionKey]);

  return null;
};

export const OidcSessionLogoutSignalListener = () => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const oidcAuthApi = useApi(oidcAuthApiRef);
  const signals = useApi(signalApiRef);
  const coordinator = getAppOidcSessionLogoutCoordinatorFromApis({
    oidcAuthApi,
    configApi,
  });
  const getCurrentSessionKey = useCallback(async () => {
    const { token } = await identityApi.getCredentials();
    return getOidcSessionKeyFromToken(token);
  }, [identityApi]);

  return (
    <OidcSessionLogoutSubscription
      signals={signals}
      coordinator={coordinator}
      getCurrentSessionKey={getCurrentSessionKey}
    />
  );
};

const oidcSessionLogoutFetchApiExtension = ApiBlueprint.make({
  name: 'oidc-session-logout-fetch',
  params: defineParams =>
    defineParams({
      api: fetchApiRef,
      deps: {
        configApi: configApiRef,
        discoveryApi: discoveryApiRef,
        identityApi: identityApiRef,
        oidcAuthApi: oidcAuthApiRef,
      },
      factory: ({ configApi, discoveryApi, identityApi, oidcAuthApi }) =>
        createOidcSessionFetchApi({
          configApi,
          discoveryApi,
          identityApi,
          oidcAuthApi,
        }),
    }),
});

/** @public */
export const oidcSessionLogout = createFrontendModule({
  pluginId: 'app',
  extensions: [
    oidcAuthApiExtension,
    oidcSessionLogoutFetchApiExtension,
    AppRootElementBlueprint.make({
      name: 'oidc-session-logout-signal-listener',
      params: {
        element: <OidcSessionLogoutSignalListener />,
      },
    }),
  ],
});
